import { useState } from "react";
import { Route, Routes } from "react-router-dom";

import { MobileHeading } from "features/layout/components";
import { ManageSubscriptionDialog } from "features/subscriptions/components";
import { PRICING_PLANS } from "features/subscriptions/constants";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceDetails from "common/datahooks/useWorkspaceDetails";
import type { RentedSubscription } from "common/types";

import ActivatePlanLayout from "../ActivatePlanLayout";
import CancelSubscriptionDialog from "../CancelSubscriptionDialog";
import Invoices from "../Invoices";
import PlanInvoicesToggle from "../PlanInvoicesToggle";
import SettingsScrollContent from "../SettingsScrollContent";
import AvatarOrder from "./AvatarOrder";
import AvatarPlanCard from "./AvatarPlanCard";

export default function AvatarPlan() {
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [manageOrderSubscription, setManageOrderSubscription] =
    useState<RentedSubscription | null>(null);
  const [cancelSubscription, setCancelSubscription] =
    useState<RentedSubscription | null>(null);

  const { id: workspaceId, has_active_rental_subscription: hasRental } =
    useSelectedWorkspaceContext();
  const { workspace, isLoadingWorkspace } = useWorkspaceDetails(workspaceId);

  const rentalSubscriptions = workspace?.rental_subscriptions
    ? [...workspace.rental_subscriptions].sort((a, b) => {
        const byCreatedAt = a.created_at - b.created_at;
        return byCreatedAt !== 0 ? byCreatedAt : a.id.localeCompare(b.id);
      })
    : [];

  // Invoices stay accessible for cancelled subscriptions so past invoices can still be downloaded
  const actionComponent = rentalSubscriptions.length > 0 && (
    <PlanInvoicesToggle planLabel={PRICING_PLANS.rental.name} />
  );

  return (
    <>
      <SettingsScrollContent desktopActionComponent={actionComponent}>
        {actionComponent && <MobileHeading actionComponent={actionComponent} />}
        <Routes>
          <Route
            path=""
            element={
              hasRental ? (
                <>
                  <AvatarPlanCard
                    onGetMoreAvatars={() => setIsManageOpen(true)}
                  />
                  {!isLoadingWorkspace &&
                    rentalSubscriptions
                      .filter(
                        (subscription) => subscription.status !== "cancelled",
                      )
                      .map((subscription, index) => (
                        <AvatarOrder
                          key={subscription.id}
                          orderNumber={index + 1}
                          subscription={subscription}
                          onManageClick={() =>
                            setManageOrderSubscription(subscription)
                          }
                          onCancelSubscriptionClick={() =>
                            setCancelSubscription(subscription)
                          }
                        />
                      ))}
                </>
              ) : (
                <ActivatePlanLayout
                  plan="rental"
                  onActivate={() => setIsManageOpen(true)}
                />
              )
            }
          />
          <Route path="invoices" element={<Invoices type="rental" />} />
        </Routes>
      </SettingsScrollContent>

      <ManageSubscriptionDialog
        initialState="rental"
        isOpen={isManageOpen}
        setIsOpen={setIsManageOpen}
        mixpanelSource="billing_page"
      />
      {manageOrderSubscription && (
        <ManageSubscriptionDialog
          initialState={{
            subscription: manageOrderSubscription,
            seats: manageOrderSubscription.rented_count,
          }}
          isOpen={!!manageOrderSubscription}
          setIsOpen={(open) => {
            if (!open) {
              setManageOrderSubscription(null);
            }
          }}
          mixpanelSource="billing_page"
        />
      )}
      {cancelSubscription && (
        <CancelSubscriptionDialog
          isOpen={!!cancelSubscription}
          setIsOpen={(open) => {
            if (!open) {
              setCancelSubscription(null);
            }
          }}
          shouldShowSurvey={false}
          subscriptionId={cancelSubscription.id}
          avatarCount={cancelSubscription.rented_count}
          onCancellationSuccess={() => {}} // No need to disable remove change button
        />
      )}
    </>
  );
}
